import { TextField } from "@via/pages/projects/metadata/configuration/data-import/components/fields";
import LoginPicker from "@via/pages/projects/metadata/configuration/data-import/components/login-picker";
import MappingSection from "@via/pages/projects/metadata/configuration/data-import/components/mapping-section";
import PreviewPanel from "@via/pages/projects/metadata/configuration/data-import/components/preview-panel";
import RunLog from "@via/pages/projects/metadata/configuration/data-import/components/run-log";
import ScheduleSection from "@via/pages/projects/metadata/configuration/data-import/components/schedule-section";
import SourceSection from "@via/pages/projects/metadata/configuration/data-import/components/source-section";
import {
  errorMessage,
  useCreateImportConfig,
  useImportConfig,
  useImportLogins,
  useLoadImportApiFields,
  useTestImportLogin,
  useUpdateImportConfig,
} from "@via/pages/projects/metadata/configuration/data-import/data-import.api.tsx";
import {
  emptyFormValues,
  toCreateBody,
  toFormValues,
  toUpdateBody,
  type ImportFormValues,
} from "@via/pages/projects/metadata/configuration/data-import/utils/form";
import type {
  VmetaImportConfigDetail,
  VmetaImportPlatform,
  VmetaImportTestLoginResult,
} from "common";
import { FC, useEffect, useState } from "react";

interface ImportConfigFormProps {
  canvasId: string;
  configId: string | null;
  platform: VmetaImportPlatform;
  /** The project's name, when the page already has it. */
  projectName?: string;
  /** Scrolls the run log into view once it renders, for the list's Last run cell. */
  focusRunLog?: boolean;
  onClose: () => void;
  /** Starts the page's confirmed delete flow for this saved import. */
  onDelete: (target: { id: string; name: string }) => void;
}

const buttonClass =
  "rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50";

const ImportConfigForm: FC<ImportConfigFormProps> = ({
  canvasId,
  configId,
  platform,
  projectName,
  focusRunLog,
  onClose,
  onDelete,
}) => {
  const [currentId, setCurrentId] = useState<string | null>(configId);
  const { data: detail } = useImportConfig(canvasId, currentId);
  const [values, setValues] = useState<ImportFormValues>(() =>
    emptyFormValues(platform)
  );
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [lastUpdateDate, setLastUpdateDate] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [loginResult, setLoginResult] =
    useState<VmetaImportTestLoginResult | null>(null);
  const [apiFields, setApiFields] = useState<string[]>([]);
  const [fieldsError, setFieldsError] = useState<string | null>(null);

  const { data: logins } = useImportLogins(true);
  const { mutateAsync: create, isPending: isCreating } =
    useCreateImportConfig();
  const { mutateAsync: update, isPending: isUpdating } =
    useUpdateImportConfig();
  const { mutateAsync: testLogin, isPending: isTesting } = useTestImportLogin();
  const { mutateAsync: loadFields, isPending: isLoadingFields } =
    useLoadImportApiFields();

  const adopt = (d: VmetaImportConfigDetail) => {
    const loaded = toFormValues(d);
    setValues(loaded);
    setLastUpdateDate(d.lastUpdateDate);
    setLoadedId(d._id);
  };

  useEffect(() => {
    if (detail && detail._id !== loadedId) adopt(detail);
  }, [detail, loadedId]);

  const runLogShown = !!currentId && !!detail;
  useEffect(() => {
    if (focusRunLog && runLogShown)
      document
        .getElementById("import-run-log-heading")
        ?.scrollIntoView({ block: "start" });
  }, [focusRunLog, runLogShown]);

  const patch = (change: Partial<ImportFormValues>) => {
    setSaved(false);
    setValues(v => ({ ...v, ...change }));
  };

  const onSave = async () => {
    setRefusal(null);
    setSaved(false);
    try {
      const result = currentId
        ? await update({
            canvasId,
            id: currentId,
            body: toUpdateBody(values, lastUpdateDate),
          })
        : await create({ canvasId, body: toCreateBody(values) });
      adopt(result);
      setCurrentId(result._id);
      setSaved(true);
    } catch (err) {
      setRefusal(errorMessage(err, "The import could not be saved."));
    }
  };

  const onTestLogin = async () => {
    if (!currentId) return;
    setLoginResult(null);
    try {
      setLoginResult(await testLogin({ canvasId, id: currentId }));
    } catch (err) {
      setLoginResult({
        ok: false,
        reason: errorMessage(err, "The login test could not run."),
      });
    }
  };

  const onLoadFields = async () => {
    if (!currentId) return;
    setFieldsError(null);
    try {
      const result = await loadFields({ canvasId, id: currentId });
      if (result.ok) setApiFields(result.fields);
      else setFieldsError(result.reason);
    } catch (err) {
      setFieldsError(errorMessage(err, "The API fields could not be loaded."));
    }
  };

  return (
    <div className="h-full space-y-8 overflow-y-auto p-4">
      <TextField
        id="import-name"
        label="Name"
        value={values.name}
        onChange={name => patch({ name })}
      />

      {detail && (
        <p className="text-sm text-gray-700">
          {detail.ownerName || detail.ownerEmail
            ? `Owner: ${detail.ownerName || detail.ownerEmail}${detail.ownerName && detail.ownerEmail ? ` (${detail.ownerEmail})` : ""}`
            : "Owner: unknown owner"}
        </p>
      )}
      {detail?.credentialName && (
        <p className="text-sm text-gray-700">
          {`Sign in: ${detail.credentialName} (${
            detail.credentialScope === "organization"
              ? `organization, ${detail.credentialOrganizationName ?? "unknown organization"}`
              : `personal, ${detail.credentialOwnerName ?? "unknown owner"}`
          })`}
        </p>
      )}
      <LoginPicker
        platform={values.platform}
        value={values.credentialId}
        items={logins?.items ?? []}
        boundName={detail?.credentialName}
        onChange={credentialId => patch({ credentialId })}
      />

      <div className="space-y-2">
        <button
          type="button"
          className={buttonClass}
          disabled={!currentId || isTesting}
          onClick={onTestLogin}>
          Test login
        </button>
        <p className="text-xs text-gray-500">
          {currentId
            ? "Uses the saved settings and the import owner's access to the credential."
            : "Save the import first."}
        </p>
        {loginResult?.ok === true && (
          <p role="status" className="text-sm text-green-700">
            Login OK
          </p>
        )}
        {loginResult?.ok === false && (
          <p role="alert" className="text-sm text-red-700">
            Login failed: {loginResult.reason}
          </p>
        )}
      </div>

      <SourceSection values={values} onChange={patch} />
      <MappingSection
        canvasId={canvasId}
        values={values}
        onChange={patch}
        apiFields={apiFields}
        canLoadFields={!!currentId}
        onLoadFields={onLoadFields}
        loadingFields={isLoadingFields}
        fieldsError={fieldsError}
      />
      <ScheduleSection values={values} onChange={patch} />

      {refusal && (
        <p
          role="alert"
          className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {refusal}
        </p>
      )}
      {saved && (
        <p role="status" className="text-sm text-green-700">
          Saved.
        </p>
      )}
      <div className="flex gap-3">
        <button
          type="button"
          disabled={
            isCreating ||
            isUpdating ||
            (!currentId && values.credentialId === null)
          }
          onClick={onSave}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">
          Save
        </button>
        <button type="button" className={buttonClass} onClick={onClose}>
          Close
        </button>
        {!currentId && values.credentialId === null && (
          <p className="text-xs text-gray-500">Choose a credential first.</p>
        )}
        {currentId && (
          <button
            type="button"
            onClick={() =>
              onDelete({ id: currentId, name: detail?.name ?? values.name })
            }
            className="ml-auto rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500">
            Delete this import
          </button>
        )}
      </div>
      {currentId && <PreviewPanel canvasId={canvasId} configId={currentId} />}
      {currentId && (
        <RunLog
          logs={detail?.logs ?? []}
          canvasId={canvasId}
          configId={currentId}
          configName={detail?.name ?? values.name}
          projectName={projectName}
          platform={platform}
        />
      )}
    </div>
  );
};

export default ImportConfigForm;
